import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useTheme as useNextTheme } from 'next-themes';
import { useProfile } from '@/hooks/useProfile';
import { useSubscription } from '@/hooks/useSubscription';
import { 
  THEME_DEFINITIONS, 
  DEFAULT_THEME, 
  DEFAULT_DARK_THEME,
  resolveLegacyTheme,
  type ThemeDefinition,
  type ThemeTokens 
} from '@/lib/theme-definitions';

const STORAGE_KEY = 'finora-theme';
const REDUCE_MOTION_KEY = 'finora-reduce-motion';

interface ThemeContextType {
  currentTheme: string;
  themeDefinition: ThemeDefinition | null;
  setTheme: (themeId: string) => boolean;
  previewTheme: (themeId: string | null) => void;
  isPreviewActive: boolean;
  canUseTheme: (themeId: string) => boolean;
  effectiveTheme: string;
  reduceMotion: boolean;
  toggleReduceMotion: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function applyThemeTokens(tokens: ThemeTokens) {
  const root = document.documentElement;
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
}

export function FinoraThemeProvider({ children }: { children: React.ReactNode }) {
  const { profile, updateProfile } = useProfile();
  const { isPro, isFullPremium } = useSubscription();
  const { setTheme: setNextTheme } = useNextTheme();
  
  const [currentTheme, setCurrentTheme] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
      return resolveLegacyTheme(stored);
    }
    return DEFAULT_THEME;
  });
  
  const [previewThemeId, setPreviewThemeId] = useState<string | null>(null);
  const [previewTimeout, setPreviewTimeout] = useState<NodeJS.Timeout | null>(null);

  const [reduceMotion, setReduceMotion] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(REDUCE_MOTION_KEY);
      if (stored !== null) return stored === 'true';
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  });

  const toggleReduceMotion = useCallback(() => {
    setReduceMotion(prev => {
      const next = !prev;
      localStorage.setItem(REDUCE_MOTION_KEY, String(next));
      return next;
    });
  }, []);

  const effectiveTheme = useMemo(() => {
    const activeTheme = previewThemeId || currentTheme;
    return THEME_DEFINITIONS[activeTheme] ? activeTheme : DEFAULT_THEME;
  }, [currentTheme, previewThemeId]);

  const themeDefinition = useMemo(() => {
    return THEME_DEFINITIONS[effectiveTheme] || THEME_DEFINITIONS[DEFAULT_THEME];
  }, [effectiveTheme]);

  // Three-tier access check
  const canUseTheme = useCallback((themeId: string): boolean => {
    const theme = THEME_DEFINITIONS[themeId];
    if (!theme) return false;
    if (theme.tier === 'free') return true;
    if (theme.tier === 'pro') return isPro || isFullPremium;
    if (theme.tier === 'premium') return isFullPremium;
    return false;
  }, [isPro, isFullPremium]);

  // Apply theme tokens
  useEffect(() => {
    const theme = THEME_DEFINITIONS[effectiveTheme];
    if (theme) {
      document.documentElement.classList.remove('no-theme-transition');
      applyThemeTokens(theme.tokens);
      document.documentElement.setAttribute('data-finora-theme', effectiveTheme);
      const isDark = theme.category === 'dark';
      setNextTheme(isDark ? 'dark' : 'light');
    }
  }, [effectiveTheme, setNextTheme]);

  // Sync with profile on load (with legacy migration)
  useEffect(() => {
    if (profile?.theme_preference) {
      const resolved = resolveLegacyTheme(profile.theme_preference);
      if (THEME_DEFINITIONS[resolved] && canUseTheme(resolved)) {
        setCurrentTheme(resolved);
        localStorage.setItem(STORAGE_KEY, resolved);
      } else if (!canUseTheme(resolved)) {
        const fallback = THEME_DEFINITIONS[resolved]?.category === 'dark' 
          ? DEFAULT_DARK_THEME 
          : DEFAULT_THEME;
        setCurrentTheme(fallback);
        localStorage.setItem(STORAGE_KEY, fallback);
      }
    }
  }, [profile?.theme_preference, canUseTheme]);

  // Revert if subscription drops
  useEffect(() => {
    const theme = THEME_DEFINITIONS[currentTheme];
    if (!theme) return;
    if (
      (theme.tier === 'pro' && !isPro && !isFullPremium) ||
      (theme.tier === 'premium' && !isFullPremium)
    ) {
      const fallback = theme.category === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_THEME;
      setCurrentTheme(fallback);
      localStorage.setItem(STORAGE_KEY, fallback);
      updateProfile({ theme_preference: fallback });
    }
  }, [isPro, isFullPremium, currentTheme, updateProfile]);

  const setTheme = useCallback((themeId: string): boolean => {
    const theme = THEME_DEFINITIONS[themeId];
    if (!theme) return false;
    if (!canUseTheme(themeId)) return false;
    
    setCurrentTheme(themeId);
    localStorage.setItem(STORAGE_KEY, themeId);
    updateProfile({ theme_preference: themeId });
    return true;
  }, [canUseTheme, updateProfile]);

  const previewTheme = useCallback((themeId: string | null) => {
    if (previewTimeout) {
      clearTimeout(previewTimeout);
      setPreviewTimeout(null);
    }
    if (themeId === null) {
      setPreviewThemeId(null);
    } else {
      setPreviewThemeId(themeId);
      const timeout = setTimeout(() => setPreviewThemeId(null), 2000);
      setPreviewTimeout(timeout);
    }
  }, [previewTimeout]);

  useEffect(() => {
    return () => {
      if (previewTimeout) clearTimeout(previewTimeout);
    };
  }, [previewTimeout]);

  // Disable transitions on initial load
  useEffect(() => {
    document.documentElement.classList.add('no-theme-transition');
    const timeout = setTimeout(() => {
      document.documentElement.classList.remove('no-theme-transition');
    }, 100);
    return () => clearTimeout(timeout);
  }, []);

  const value = useMemo(() => ({
    currentTheme,
    themeDefinition,
    setTheme,
    previewTheme,
    isPreviewActive: previewThemeId !== null,
    canUseTheme,
    effectiveTheme,
    reduceMotion,
    toggleReduceMotion,
  }), [currentTheme, themeDefinition, setTheme, previewTheme, previewThemeId, canUseTheme, effectiveTheme, reduceMotion, toggleReduceMotion]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useFinoraTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useFinoraTheme must be used within a FinoraThemeProvider');
  }
  return context;
}
